import { Component } from 'react'
import { AlertTriangle, RotateCcw } from 'lucide-react'

/**
 * A stale-bundle failure, not a bug in the code.
 *
 * Every deploy replaces the content-hashed chunk files. A tab that was loaded
 * BEFORE the deploy still holds the old hashes, so the moment it lazy-loads a
 * chunk — which App.js does at startup, since the staff and portal shells are
 * split — the request 404s and React throws into the outermost boundary.
 *
 * Charles hit this repeatedly in late October 2026. It looked like a login
 * failure and was reported as one, which is why the auth logs showed nothing:
 * there was no failed sign-in anywhere, his token was refreshing normally on
 * an open tab, and seven deploys landed underneath it in three days.
 *
 * The headers are not the problem and should not be "fixed": index.html is
 * served `max-age=0, must-revalidate` and the chunks `immutable`, which is
 * correct. A reload genuinely repairs it — the user simply should not have to
 * know that.
 */
const CHUNK_ERROR = /Loading chunk [\w-]+ failed|Loading CSS chunk|ChunkLoadError|dynamically imported module/i

export function isStaleBundleError(error) {
  if (!error) return false
  return error.name === 'ChunkLoadError' || CHUNK_ERROR.test(String(error.message || ''))
}

// One automatic reload per tab. Without the guard a genuinely missing chunk —
// a broken deploy rather than a stale one — would reload forever, which is a
// worse failure than the error screen and harder to diagnose.
const RELOAD_FLAG = 'compass:stale-bundle-reloaded'

export function shouldAutoReload(error, storage) {
  if (!isStaleBundleError(error)) return false
  // No storage means no way to count attempts, so there is no guard — and an
  // unguarded reload is an infinite one. Optional chaining here was a real bug:
  // `storage?.getItem(...)` is undefined, `storage?.setItem(...)` is a no-op,
  // and the function returned true every single time.
  if (!storage) return false
  try {
    if (storage.getItem(RELOAD_FLAG)) return false
    storage.setItem(RELOAD_FLAG, String(Date.now()))
    return true
  } catch {
    // Private mode, blocked storage: show the error rather than risk a loop.
    return false
  }
}

// Called once the app boots successfully, so the NEXT stale bundle is also
// recovered automatically rather than only the first one ever.
export function clearStaleBundleFlag(storage) {
  try { storage?.removeItem(RELOAD_FLAG) } catch { /* nothing to clear */ }
}

/**
 * Catches a render-time crash so one broken component degrades instead of
 * blanking the page.
 *
 * This exists because of a real outage: `admin is not defined` in
 * OnboardingChecklist.js reached production and blanked the client page for
 * anyone with a grandfathered client. React unmounts the whole tree when a
 * render throws, so a single undefined variable took out everything — nav
 * included — and left a white screen with no clue what happened.
 *
 * Mounted in two places, deliberately:
 *   - `src/index.js`, outermost, so a crash in App or AuthContext still shows
 *     something a human can read.
 *   - inside the `<main>` of each shell (Dashboard, PortalLayout), so a crash
 *     in one page keeps the sidebar alive and stays survivable by clicking
 *     somewhere else.
 *
 * Pass `resetKey` (the route path) so navigating away clears a caught error.
 * Without it the fallback would persist for the rest of the session: the
 * boundary has no idea the user asked for a different page.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error, info) {
    // The component stack is the part that actually locates the bug, and React
    // does not include it in the error itself. This is also the seam a reporter
    // (Sentry or similar) hooks into later — one call, one place.
    console.error('Render error caught by ErrorBoundary:', error)
    if (info?.componentStack) {
      console.error('Component stack:', info.componentStack)
    }

    // A stale bundle is not a bug — it is a tab older than the deploy. Reload
    // once, automatically, instead of showing a dead end that says "reload".
    if (shouldAutoReload(error, typeof window !== 'undefined' ? window.sessionStorage : null)) {
      console.warn('Stale bundle detected; reloading once to pick up the new deploy.')
      window.location.reload()
    }
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null })
    }
  }

  handleRetry = () => {
    this.setState({ error: null })
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    const { fullScreen = false, title = 'This section could not be displayed' } =
      this.props

    const card = (
      <div className="bg-navy rounded-2xl shadow-2xl overflow-hidden max-w-xl w-full">
        <div className="h-1 bg-gradient-to-r from-gold via-[#D4A843] to-gold/10" />
        <div className="p-5 md:p-7">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-gold shrink-0 mt-0.5" />
            <div className="min-w-0">
              <h2 className="font-oswald text-xl md:text-2xl text-white uppercase tracking-wide">
                {title}
              </h2>
              <p className="font-inter text-sm text-brand-grey mt-2">
                {fullScreen
                  ? 'The app ran into a problem while starting up. Reloading clears this most of the time.'
                  : 'This part of the page failed to load. The rest of the app is still working, so you can carry on elsewhere.'}
              </p>

              {error.message ? (
                <p className="font-mono text-xs text-gold/80 bg-black/30 rounded px-3 py-2 mt-4 break-words">
                  {error.message}
                </p>
              ) : null}

              <div className="flex flex-wrap gap-3 mt-5">
                <button
                  onClick={this.handleRetry}
                  className="inline-flex items-center gap-2 bg-gold text-navy font-inter font-semibold text-sm px-4 py-2 rounded-lg hover:bg-[#D4A843] transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  Try again
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="font-inter text-sm text-brand-grey underline hover:text-white transition-colors px-2 py-2"
                >
                  Reload the page
                </button>
              </div>

              <p className="font-inter text-xs text-brand-grey/70 mt-5">
                If it keeps happening, send the line above to Mike — it names
                the fault.
              </p>
            </div>
          </div>
        </div>
      </div>
    )

    if (fullScreen) {
      return (
        <div className="min-h-screen bg-off-white flex items-center justify-center p-4">
          {card}
        </div>
      )
    }

    return <div className="flex justify-center py-6">{card}</div>
  }
}

export default ErrorBoundary
